/* eslint-disable react/prop-types */
import {
  FormControl,
  FormLabel,
  Input,
  Stack,
  useColorMode,
} from "@chakra-ui/react";
import { UpdateMerchantBankDetailsPayload } from "redux/actions/admin-merchants";
import WarningTextComponent, {
  WarningTextProps,
} from "components/shared/WarningTextComponent";
import {
  Button,
  ButtonSize,
  ButtonType,
} from "theme/old-design-system/styled-components";
import useAdminMerchantStore from "hooks/use-admin-merchant-store";
import useCustomChakraTheme from "hooks/use-custom-chakra-theme";
import React, { ChangeEvent, useEffect, useState } from "react";
import Select, { SingleValue } from "react-select";
import { toast } from "react-toastify";
import {
  BankAccountType,
  BankDetailsType,
  MerchantDto,
} from "entities/dto/MerchantDTO";
import { getReactSelectStyles } from "util/methods";

interface MerchantOptionTypes {
  value?: number;
  label?: string;
}

interface CountryOptionTypes {
  value: string;
  label: string;
}

const BankDetails = () => {
  const { adminMerchants, editMerchantBankDetails, errors } =
    useAdminMerchantStore();
  const { colorMode } = useColorMode();
  const { colorScheme } = useCustomChakraTheme();

  const merchantOptions: MerchantOptionTypes[] = adminMerchants.map(
    (merchant: MerchantDto) => ({
      value: merchant.id,
      label: merchant.name,
    })
  );

  const countryOptions: CountryOptionTypes[] = [
    { value: "GB", label: "United Kingdom" },
    { value: "IE", label: "Ireland" },
  ];

  const [merchantId, setMerchantId] = useState<number>(0);
  const [selectedMerchantValue, setSelectedMerchantValue] =
    useState<SingleValue<MerchantOptionTypes>>(null);
  const [countryValue, setCountryValue] = useState<string>(
    countryOptions[0].value
  );
  const [selectedCountryValue, setSelectedCountryValue] = useState<
    SingleValue<CountryOptionTypes>
  >(countryOptions[0]);
  const [beneficiaryNameValue, setBeneficiaryNameValue] = useState<string>("");
  const [bankAccountNumValue, setBankAccountNumValue] = useState<string>("");
  const [sortCodeValue, setSortCodeValue] = useState<string>("");
  const [ibanValue, setIbanValue] = useState<string>("");
  const [bicValue, setBicValue] = useState<string>("");
  const [warning, setWarning] = useState<WarningTextProps | undefined>(
    undefined
  );

  useEffect(() => {
    setWarning(undefined);
  }, []);

  const isValidBankDetails = () => {
    if (
      bankAccountNumValue !== "" &&
      (bankAccountNumValue.length < 7 || bankAccountNumValue.length > 8)
    ) {
      setWarning({
        key: "invalid_bank_acct",
        description: "* Bank Account Number should be between 7 and 8 digits",
      });
    } else if (sortCodeValue !== "" && sortCodeValue.length !== 6) {
      setWarning({
        key: "invalid_sort_code",
        description: "* Sort code should be 6 digits",
      });
    } else if (ibanValue !== "" && ibanValue.length >= 34) {
      setWarning({
        key: "invalid_iban",
        description: " * Iban should not be more than 34 characters",
      });
    } else if (bicValue !== "" && bicValue.length < 8) {
      setWarning({
        key: "invalid_bic",
        description: "* BIC should not be less than 8 characters",
      });
    } else if (bicValue.length > 11) {
      setWarning({
        key: "invalid_bic",
        description: "* BIC should not be more than 11 characters",
      });
    } else {
      setWarning(undefined);
    }
  };

  const handleIdChange = (e: SingleValue<MerchantOptionTypes>) => {
    setMerchantId(Number(e?.value));
    setSelectedMerchantValue(e);
  };

  const handleOnCountryChange = (e: SingleValue<CountryOptionTypes>) => {
    setCountryValue(e!.value);
    setSelectedCountryValue(e);
  };

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) =>
    setBeneficiaryNameValue(event.target.value);

  const handleAcctNumChange = (event: ChangeEvent<HTMLInputElement>) =>
    setBankAccountNumValue(event.target.value);

  const handleSortCodeChange = (event: ChangeEvent<HTMLInputElement>) =>
    setSortCodeValue(event.target.value);

  const handleIbanChange = (event: ChangeEvent<HTMLInputElement>) =>
    setIbanValue(event.target.value);

  const handleBicChange = (event: ChangeEvent<HTMLInputElement>) =>
    setBicValue(event.target.value);

  const validBankAcctCheck =
    bankAccountNumValue.length === 7 || bankAccountNumValue.length === 8;

  const invalidBicCheck = bicValue.length >= 8 && bicValue.length <= 11;

  const isValidInfoGB = !validBankAcctCheck || sortCodeValue.length !== 6;

  const isValidInfoEU = !invalidBicCheck || !(ibanValue.length <= 34);

  const isValidBankInfo = countryValue === "GB" ? isValidInfoGB : isValidInfoEU;

  const isDisabled = (): boolean =>
    !merchantId || !beneficiaryNameValue || isValidBankInfo || !!warning;

  const bankDetailsObj: BankDetailsType = {};

  const bankAcctObj: BankAccountType = {
    country: countryValue,
    beneficiary_name: beneficiaryNameValue,
    details: bankDetailsObj,
  };

  const updateMerchant = () => {
    if (countryValue === "GB") {
      bankDetailsObj.account_number = bankAccountNumValue;
      bankDetailsObj.sort_code = sortCodeValue;
    }

    if (countryValue !== "GB") {
      bankDetailsObj.bic = bicValue;
      bankDetailsObj.iban = ibanValue;
    }

    const updateMerchantObj: UpdateMerchantBankDetailsPayload = {
      merchantId: Number(merchantId),
      bank_account: bankAcctObj,
    };

    editMerchantBankDetails(updateMerchantObj);
  };

  const resetValues = () => {
    setMerchantId(Number(null));
    setSelectedMerchantValue(null);
    setCountryValue(countryOptions[0].value);
    setBeneficiaryNameValue("");
    setBankAccountNumValue("");
    setSortCodeValue("");
  };

  const handleSaveMerchant = () => {
    updateMerchant();
    resetValues();
    if (errors.length) {
      toast.error(errors[0]);
    } else {
      toast.success("Merchant Updated Successfully");
    }
  };

  return (
    <div>
      <Stack spacing={3}>
        <FormControl isRequired>
          <FormLabel> Select Merchant </FormLabel>
          <Select
            placeholder="Select Merchants"
            onChange={handleIdChange}
            options={merchantOptions}
            value={selectedMerchantValue}
            styles={getReactSelectStyles(colorMode, colorScheme)}
          />
        </FormControl>
        <FormControl isRequired>
          <FormLabel> Country </FormLabel>
          <Select
            placeholder="Select Country"
            onChange={handleOnCountryChange}
            options={countryOptions}
            value={selectedCountryValue}
            styles={getReactSelectStyles(colorMode, colorScheme)}
          />
        </FormControl>
        <FormControl isRequired>
          <FormLabel mb="8px"> Beneficiary Name</FormLabel>
          <Input
            value={beneficiaryNameValue}
            onChange={handleNameChange}
            placeholder="John Doe"
          />
        </FormControl>
        <FormControl
          isRequired
          display={countryValue === "GB" ? "initial" : "none"}
        >
          <FormLabel mb="8px"> Bank Account Number</FormLabel>
          {warning && warning.key === "invalid_bank_acct" && (
            <WarningTextComponent isValid={false} text={warning.description} />
          )}
          <Input
            value={bankAccountNumValue}
            onChange={handleAcctNumChange}
            placeholder="xxxxxxxx"
            onBlur={isValidBankDetails}
          />
        </FormControl>
        <FormControl
          isRequired
          display={countryValue === "GB" ? "initial" : "none"}
        >
          <FormLabel mb="8px"> Bank Sort Code</FormLabel>
          {warning && warning.key === "invalid_sort_code" && (
            <WarningTextComponent isValid={false} text={warning.description} />
          )}
          <Input
            value={sortCodeValue}
            onChange={handleSortCodeChange}
            placeholder="xxxxxx"
            onBlur={isValidBankDetails}
          />
        </FormControl>
        <FormControl
          isRequired
          display={countryValue !== "GB" ? "initial" : "none"}
        >
          <FormLabel mb="8px"> IBAN </FormLabel>
          {warning && warning.key === "invalid_iban" && (
            <WarningTextComponent isValid={false} text={warning.description} />
          )}
          <Input
            value={ibanValue}
            onChange={handleIbanChange}
            placeholder="IExxxxxxxxxxxxxxxxxxxx"
            onBlur={isValidBankDetails}
          />
        </FormControl>
        <FormControl
          isRequired
          display={countryValue !== "GB" ? "initial" : "none"}
        >
          <FormLabel mb="8px"> Bank Identifier Code (BIC) </FormLabel>

          {warning && warning.key === "invalid_bic" && (
            <WarningTextComponent isValid={false} text={warning.description} />
          )}
          <Input
            value={bicValue}
            onChange={handleBicChange}
            placeholder="xxxxxx"
            onBlur={isValidBankDetails}
          />
        </FormControl>
      </Stack>
      <Button
        type={ButtonType.PRIMARY}
        onClick={handleSaveMerchant}
        isDisabled={isDisabled()}
        mb={30}
        mt={30}
        pl={10}
        pr={10}
        size={ButtonSize.LARGE}
      >
        Save Information
      </Button>
    </div>
  );
};

export default BankDetails;
